import { getDefaultOperator, getOperatorArity, getValueType, normalizeOperator } from "./operators.js";
import type { FieldMetadata, FilterExpression, FilterGroup, FilterOperator, FilterTree } from "./types.js";

/**
 * Path to a node in a filter tree, as a list of item indexes from the root
 * group. The root itself is the empty path.
 *
 * @example
 * ```ts
 * // The second condition of the first group
 * let path: FilterPath = [0, 1];
 * ```
 */
export type FilterPath = number[];

export type FilterNode = FilterGroup | FilterExpression;

/**
 * Creates the default empty filter: a single `and` group with no conditions.
 */
export function createEmptyFilter(): FilterTree {
	return createEmptyGroup("and");
}

export function createEmptyGroup(mode: FilterGroup["mode"] = "and"): FilterGroup {
	return { type: "group", mode, items: [] };
}

export function isGroup(item: FilterNode): item is FilterGroup {
	return item.type === "group";
}

export function isExpression(item: FilterNode): item is FilterExpression {
	return item.type === "expression";
}

/**
 * Normalizes any filter value into a tree the editor can render, without
 * changing its structure. An expression is wrapped in a group; a missing filter
 * becomes the default layout.
 */
export function toFilterTree(filter: FilterNode | null | undefined): FilterTree {
	if (!filter) return createEmptyFilter();
	if (isExpression(filter)) return { type: "group", mode: "and", items: [filter] };

	return filter;
}

/**
 * True when the filter contains no expressions. An empty filter must not be
 * saved or applied.
 */
export function isEmptyFilter(filter: FilterNode | null | undefined): boolean {
	return countExpressions(filter) === 0;
}

export function countExpressions(filter: FilterNode | null | undefined): number {
	if (!filter) return 0;
	if (isExpression(filter)) return 1;

	return filter.items.reduce((total, item) => total + countExpressions(item), 0);
}

/**
 * Removes incomplete expressions and empty groups from the whole tree. Returns
 * null when nothing is left. Use before applying a filter to a data object so
 * incomplete rows are not sent.
 */
export function pruneFilter(filter: FilterNode | null | undefined): FilterGroup | null {
	if (!filter) return null;

	if (isExpression(filter)) {
		return isCompleteExpression(filter) ? { type: "group", mode: "and", items: [filter] } : null;
	}

	let items = filter.items
		.map((item) => (isGroup(item) ? pruneFilter(item) : isCompleteExpression(item) ? item : null))
		.filter((item): item is FilterNode => item !== null);

	if (items.length === 0) return null;

	return { ...filter, items };
}

/**
 * True when an expression has everything it needs to be sent to the server.
 */
export function isCompleteExpression(item: FilterNode): boolean {
	if (isGroup(item)) return item.items.some(isCompleteExpression);
	if (!item.column || !item.operator) return false;

	let arity = getOperatorArity(normalizeOperator(item.operator));

	if (arity === "none") return true;
	if (item.value === null || item.value === undefined || item.value === "") return false;

	if (arity === "pair") {
		return Array.isArray(item.value) && item.value.length === 2 && item.value.every((v) => v !== "" && v != null);
	}

	if (arity === "list") {
		return Array.isArray(item.value) && item.value.length > 0;
	}

	return true;
}

/**
 * Creates an expression for a field, using the field's default operator and an
 * empty value of the right shape.
 */
export function createExpression(field: FieldMetadata, operator?: FilterOperator): FilterExpression {
	let op = operator ?? getDefaultOperator(field);

	return {
		type: "expression",
		column: field.name,
		operator: op,
		value: createEmptyValue(op),
		valueType: getValueType(field, op),
	};
}

function createEmptyValue(operator: FilterOperator): FilterExpression["value"] {
	switch (getOperatorArity(operator)) {
		case "none":
			return null;
		case "pair":
			return ["", ""];
		case "list":
			return [];
		default:
			return "";
	}
}

/**
 * Returns a copy of the expression with a new operator, adjusting the value to
 * the new operator's arity and the value type accordingly.
 */
export function changeOperator(
	expression: FilterExpression,
	operator: FilterOperator,
	field: Pick<FieldMetadata, "type">,
): FilterExpression {
	let previousArity = getOperatorArity(normalizeOperator(expression.operator));
	let arity = getOperatorArity(operator);
	let value = expression.value;

	if (arity !== previousArity) {
		value = createEmptyValue(operator);
	}

	// A dynamic value stays valid across operators of the same arity.
	let valueType =
		expression.valueType === "special" && arity === previousArity ? "special" : getValueType(field, operator);

	return { ...expression, operator, value, valueType };
}

/** Returns the node at `path`, or null when the path does not exist. */
export function getNodeAtPath(filter: FilterNode, path: FilterPath): FilterNode | null {
	let node: FilterNode | undefined = filter;

	for (let index of path) {
		if (!node || !isGroup(node)) return null;
		node = node.items[index];
	}

	return node ?? null;
}

/**
 * Returns a copy of the tree with `updater` applied to the node at `path`.
 * Returning null removes the node. An unknown path leaves the tree untouched.
 */
export function updateNodeAtPath<T extends FilterNode>(
	filter: T,
	path: FilterPath,
	updater: (node: FilterNode) => FilterNode | null,
): T {
	if (path.length === 0) {
		return (updater(filter) as T | null) ?? filter;
	}

	if (!isGroup(filter)) return filter;

	let [index, ...rest] = path;
	let child = filter.items[index as number];
	if (!child) return filter;

	let items = [...filter.items];

	if (rest.length === 0) {
		let updated = updater(child);

		if (updated === null) {
			items.splice(index as number, 1);
		} else {
			items[index as number] = updated;
		}
	} else {
		items[index as number] = updateNodeAtPath(child, rest, updater);
	}

	return { ...filter, items };
}

/** Returns a copy of the tree with `node` appended to the group at `path`. */
export function insertNodeAtPath<T extends FilterNode>(filter: T, path: FilterPath, node: FilterNode): T {
	return updateNodeAtPath(filter, path, (target) =>
		isGroup(target) ? { ...target, items: [...target.items, node] } : target,
	);
}

/**
 * Returns a copy of the tree with the node at `path` removed. The root is never
 * removed; it is reset to the default layout instead.
 */
export function removeNodeAtPath<T extends FilterNode>(filter: T, path: FilterPath): T {
	if (path.length === 0) return createEmptyFilter() as T;

	return updateNodeAtPath(filter, path, () => null);
}

/** Returns a copy of the tree with the mode of the group at `path` changed. */
export function setGroupMode<T extends FilterNode>(filter: T, path: FilterPath, mode: FilterGroup["mode"]): T {
	return updateNodeAtPath(filter, path, (node) => (isGroup(node) ? { ...node, mode } : node));
}

/** Returns a copy of the tree with an empty group appended to the group at `path`. */
export function addGroupAtPath<T extends FilterNode>(filter: T, path: FilterPath = []): T {
	let parent = getNodeAtPath(filter, path);
	// A nested group is only useful when it combines differently to its parent.
	let mode: FilterGroup["mode"] = parent && isGroup(parent) && parent.mode === "and" ? "or" : "and";

	return insertNodeAtPath(filter, path, createEmptyGroup(mode));
}
